import { type ReactNode, useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { useAuthStore } from "@/stores/auth";
import { loadTenantFontFaces } from "@/lib/tenantFonts";
import { Header } from "./Header";

interface LayoutProps {
  children: ReactNode;
  sidebar?: ReactNode;
  requireAuth?: boolean;
}

export function Layout({ children, sidebar, requireAuth = true }: LayoutProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const router = useRouter();

  useEffect(() => {
    if (requireAuth && !isAuthenticated) {
      void router.navigate({ to: "/login" });
    }
  }, [requireAuth, isAuthenticated, router]);

  // Register per-tenant brand fonts once we're authenticated — covers both a
  // fresh boot with a persisted token and a post-login transition. Idempotent
  // (fetch is cached, faces already registered are skipped), so re-running on
  // route changes is cheap.
  useEffect(() => {
    if (isAuthenticated) void loadTenantFontFaces();
  }, [isAuthenticated]);

  if (requireAuth && !isAuthenticated) {
    return null;
  }

  return (
    <div className="flex h-screen flex-col bg-surface">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        {sidebar}
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
